import { http, HttpResponse } from "msw"
import {
  type SpecificUniverseName,
  type UniverseName,
  specificUniverseEnum,
  universeEnum,
} from "../../../types"
import type { ApiSales } from "../../sales/root"
import type { ApiSpecificCategorySalesDetails } from "../../sales/specificCategorySalesDetails/specificCategorySalesDetails.model"
import type { ApiSpecificSales } from "../../sales/specificSales/specificSales.model"
import type { ApifinancialDataSalesSummary } from "../../sales/summary/financialDataSalesSummary.model"
import type { ApiUniverseSalesDetails } from "../../sales/universeSalesDetails/universeSalesDetails.model"
import type { ApiUniverseSales } from "../../sales/universesSales/universesSales.model"
import {
  mockSales,
  salesSummaryMock,
  specificCategorySalesDetailsMock,
  specificSalesMock,
  universeSalesDetailsMock,
  universesSalesMock,
} from "./sales.mock"

const API_DOMAIN = import.meta.env.VITE_API_DOMAIN

export const financialDataSalesHandlers = [
  // ADHERENT_HOLDER & ADHERENT_TEAM
  http.get(`${API_DOMAIN}/api/financial-data/adherent/sales`, () => {
    return HttpResponse.json<ApiSales>(mockSales())
  }),
  http.get(`${API_DOMAIN}/api/financial-data/adherent/sales/universes`, () => {
    return HttpResponse.json<ApiUniverseSales>(universesSalesMock())
  }),
  http.get(
    `${API_DOMAIN}/api/financial-data/adherent/sales/specific-categories`,
    () => {
      return HttpResponse.json<ApiSpecificSales>(specificSalesMock())
    },
  ),

  // ADHERENT_ADVISOR
  http.get(
    `${API_DOMAIN}/api/financial-data/advisor/pharmacies/:pharmacyId/sales`,
    () => {
      return HttpResponse.json<ApiSales>(mockSales())
    },
  ),
  http.get(
    `${API_DOMAIN}/api/financial-data/advisor/pharmacies/:pharmacyId/sales/specific-categories`,
    () => {
      return HttpResponse.json<ApiSpecificSales>(specificSalesMock())
    },
  ),
  http.get(
    `${API_DOMAIN}/api/financial-data/advisor/pharmacies/:pharmacyId/sales/universes`,
    () => {
      return HttpResponse.json<ApiUniverseSales>(universesSalesMock())
    },
  ),

  // ADMIN
  http.get(`${API_DOMAIN}/api/financial-data/pharmacies/:pharmacyId/sales`, () => {
    return HttpResponse.json<ApiSales>(mockSales())
  }),
  http.get(
    `${API_DOMAIN}/api/financial-data/pharmacies/:pharmacyId/sales/universes`,
    () => {
      return HttpResponse.json<ApiUniverseSales>(universesSalesMock())
    },
  ),
  http.get(
    `${API_DOMAIN}/api/financial-data/pharmacies/:pharmacyId/sales/specific-categories`,
    () => {
      return HttpResponse.json<ApiSpecificSales>(specificSalesMock())
    },
  ),

  // LABO
  http.get(`${API_DOMAIN}/api/financial-data/laboratory/sales/summary`, () =>
    HttpResponse.json<ApifinancialDataSalesSummary>(salesSummaryMock()),
  ),

  // UNIVERSE DETAILS
  http.get(
    `${API_DOMAIN}/api/financial-data/adherent/sales/universes/:universeCode/families`,
    ({ params }) => universeDetailsResponse(params.universeCode),
  ),
  http.get(
    `${API_DOMAIN}/api/financial-data/pharmacies/:pharmacyId/sales/universes/:universeCode/families`,
    ({ params }) => universeDetailsResponse(params.universeCode),
  ),
  http.get(
    `${API_DOMAIN}/api/financial-data/president/pharmacies/:pharmacyId/sales/universes/:universeCode/families`,
    ({ params }) => universeDetailsResponse(params.universeCode),
  ),
  http.get(
    `${API_DOMAIN}/api/financial-data/advisor/pharmacies/:pharmacyId/sales/universes/:universeCode/families`,
    ({ params }) => universeDetailsResponse(params.universeCode),
  ),

  // SPECIFIC CATEGORY DETAILS
  http.get(
    `${API_DOMAIN}/api/financial-data/adherent/sales/specific-categories/:specificCode/families`,
    ({ params }) => specificCategoryDetailsResponse(params.specificCode),
  ),
  http.get(
    `${API_DOMAIN}/api/financial-data/pharmacies/:pharmacyId/sales/specific-categories/:specificCode/families`,
    ({ params }) => specificCategoryDetailsResponse(params.specificCode),
  ),
  http.get(
    `${API_DOMAIN}/api/financial-data/advisor/pharmacies/:pharmacyId/sales/specific-categories/:specificCode/families`,
    ({ params }) => specificCategoryDetailsResponse(params.specificCode),
  ),
]

const universeDetailsResponse = (universeCode: string | readonly string[]) => {
  if (typeof universeCode !== "string" || !(universeCode in universeEnum)) {
    return new HttpResponse(null, { status: 404 })
  }
  return HttpResponse.json<ApiUniverseSalesDetails>(
    universeSalesDetailsMock(universeCode as UniverseName),
  )
}

const specificCategoryDetailsResponse = (
  specificCode: string | readonly string[],
) => {
  if (typeof specificCode !== "string" || !(specificCode in specificUniverseEnum)) {
    return new HttpResponse(null, { status: 404 })
  }
  return HttpResponse.json<ApiSpecificCategorySalesDetails>(
    specificCategorySalesDetailsMock(specificCode as SpecificUniverseName),
  )
}
